import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO, { breadcrumbJsonLd, faqJsonLd, itemListJsonLd, SITE_URL } from '../../components/SEO';
import ComparisonTable from '../../components/comparison/ComparisonTable';
import type { ComparisonRow } from '../../components/comparison/ComparisonTable';
import FeatureSection from '../../components/comparison/FeatureSection';
import CTASection from '../../components/comparison/CTASection';
import FAQSection from '../../components/comparison/FAQSection';
import { getComparisonTheme } from '../../components/comparison/comparisonVisualThemes';
import ComparisonAICommandCentreSection from '../../components/comparison/ComparisonAICommandCentreSection';
import { COMPETITOR_ALTERNATIVES, hubAggregateComparisonRows } from './data';

const PAGE_PATH = '/compare';

const PILL_KEYS = ['command', 'live', 'stack'] as const;
const PILL_ICONS: Record<(typeof PILL_KEYS)[number], string> = {
  command: 'ri-robot-2-line',
  live: 'ri-truck-line',
  stack: 'ri-global-line',
};

/** Same asset as Navbar / Footer — primary Geofleet wordmark */
const GEOFLEET_LOGO_SVG = 'https://cdn.geofleet.ai/images/logo.svg';

const STEP_KEYS = ['overview', 'vendor', 'validate'] as const;
const STEP_ICONS: Record<(typeof STEP_KEYS)[number], string> = {
  overview: 'ri-table-line',
  vendor: 'ri-focus-3-line',
  validate: 'ri-calendar-check-line',
};

function asStringArray(v: unknown): string[] | undefined {
  if (!Array.isArray(v) || !v.every((x) => typeof x === 'string')) return undefined;
  return v as string[];
}

function asFaqArray(v: unknown): { question: string; answer: string }[] {
  if (!Array.isArray(v)) return [];
  return v.filter(
    (x): x is { question: string; answer: string } =>
      x != null && typeof x === 'object' && 'question' in x && 'answer' in x
  ) as { question: string; answer: string }[];
}

export default function CompareHubPage() {
  const { t, i18n } = useTranslation();
  const theme = getComparisonTheme('blue');
  const pageUrl = `${SITE_URL}${PAGE_PATH}`;

  const faqs = useMemo(() => asFaqArray(t('compareHub.faqs', { returnObjects: true })), [t, i18n.language]);

  const displayRows = useMemo((): ComparisonRow[] => {
    const fo = asStringArray(t('compareHub.aggregateFeatures', { returnObjects: true }));
    const go = asStringArray(t('compareHub.aggregateGeofleetCells', { returnObjects: true }));
    const co = asStringArray(t('compareHub.aggregateCompetitorCells', { returnObjects: true }));
    return hubAggregateComparisonRows.map((row, i) => ({
      feature: fo?.[i] ?? row.feature,
      geofleet: go?.[i] ?? row.geofleet,
      competitor: co?.[i] ?? row.competitor,
    }));
  }, [t, i18n.language]);

  /** Same wording as hero lockup — short column header, no duplicate long label */
  const competitorLabel = t('compareHub.hero.lockupPartner');

  return (
    <article className="pt-0 bg-white">
      <SEO
        title={t('compareHub.seo.title')}
        exactDocumentTitle
        description={t('compareHub.seo.description')}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', url: SITE_URL },
            { name: t('compareHub.breadcrumb'), url: pageUrl },
          ]),
          faqJsonLd(faqs),
          itemListJsonLd(
            'Geofleet competitor comparisons',
            COMPETITOR_ALTERNATIVES.map((d) => ({ name: d.h1, url: `${SITE_URL}/${d.pathSlug}` }))
          ),
        ]}
      />

      <section
        className="relative pt-24 pb-10 lg:pt-28 lg:pb-14 overflow-hidden"
        style={{
          backgroundColor: '#fafafa',
          backgroundImage: theme.mesh,
        }}
        aria-labelledby="compare-hub-heading"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage: `linear-gradient(135deg, ${theme.soft} 0%, transparent 42%, transparent 58%, ${theme.soft} 100%)`,
          }}
          aria-hidden
        />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 sm:gap-10 md:gap-14 mb-10">
            <Link
              to="/"
              className="inline-flex items-center shrink-0 hover:opacity-90 transition-opacity"
              aria-label="Geofleet.ai home"
            >
              <img
                src={GEOFLEET_LOGO_SVG}
                alt="Geofleet.ai"
                className="h-8 sm:h-9 md:h-10 w-auto max-w-[200px] object-contain object-left"
                width={160}
                height={40}
              />
            </Link>
            <span
              className="text-slate-400 text-sm font-semibold tracking-[0.2em] uppercase select-none"
              aria-hidden
            >
              vs
            </span>
            <span className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight text-center lg:text-left">
              {t('compareHub.hero.lockupPartner')}
            </span>
          </div>
          <div className="text-center lg:text-left max-w-3xl lg:max-w-none mx-auto lg:mx-0">
            <p
              className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] mb-5 px-3 py-1.5 rounded-full border"
              style={{
                color: theme.primary,
                borderColor: `rgba(${theme.primaryRgb}, 0.25)`,
                backgroundColor: `rgba(${theme.primaryRgb}, 0.06)`,
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: theme.primary }} />
              {t('compareHub.hero.eyebrow')}
            </p>
            <h1
              id="compare-hub-heading"
              className="text-3xl sm:text-4xl lg:text-[2.65rem] font-bold text-slate-900 leading-[1.12] tracking-tight mb-4"
            >
              {t('compareHub.hero.h1Before')}{' '}
              <span style={{ color: theme.primary }}>{t('compareHub.hero.h1Highlight')}</span>
              {t('compareHub.hero.h1After') ? <> {t('compareHub.hero.h1After')}</> : null}
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-6 lg:max-w-2xl font-medium">
              {t('compareHub.hero.leadBefore')}{' '}
              <strong className="font-semibold text-slate-800">{t('compareHub.hero.leadEmphasis1')}</strong>
              {t('compareHub.hero.leadMiddle')}{' '}
              <strong className="font-semibold text-slate-800">{t('compareHub.hero.leadEmphasis2')}</strong>
              {t('compareHub.hero.leadAfter')}
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center lg:justify-start gap-3 mb-10">
              <a
                href="#hub-comparison-table"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white shadow-lg hover:opacity-95 transition-opacity"
                style={{ backgroundColor: theme.primary }}
              >
                {t('compareHub.hero.ctaTable')}
                <i className="ri-arrow-down-line" aria-hidden />
              </a>
              <a
                href="https://app.geofleet.ai/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold border-2 border-slate-200/90 text-slate-800 hover:border-slate-300 transition-colors bg-white"
              >
                {t('compareHub.hero.ctaTrial')}
              </a>
              <a
                href="https://meetings.hubspot.com/aditya145"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors"
              >
                {t('compareHub.hero.ctaDemo')}
                <i className="ri-arrow-right-up-line text-base opacity-70" aria-hidden />
              </a>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 lg:gap-4">
            {PILL_KEYS.map((key) => (
              <div
                key={key}
                className="flex gap-3 rounded-2xl border border-slate-200/80 bg-white/90 backdrop-blur-sm px-4 py-4 text-left shadow-sm"
              >
                <span
                  className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-lg text-white shadow-sm"
                  style={{ backgroundColor: theme.primary }}
                  aria-hidden
                >
                  <i className={PILL_ICONS[key]} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{t(`compareHub.pills.${key}.title`)}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-snug">{t(`compareHub.pills.${key}.sub`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-100 bg-white" aria-label={t('compareHub.howToLabel')}>
        <div className="max-w-6xl mx-auto px-6 py-8 lg:py-10">
          <p className="text-center text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-6">
            {t('compareHub.howToLabel')}
          </p>
          <ol className="grid md:grid-cols-3 gap-6 md:gap-8 list-none p-0 m-0 not-prose">
            {STEP_KEYS.map((key, i) => (
              <li key={key} className="relative flex gap-4 md:block md:text-center">
                <span
                  className="flex md:mx-auto mb-0 md:mb-3 w-10 h-10 shrink-0 rounded-full items-center justify-center text-sm font-bold text-white"
                  style={{ backgroundColor: theme.primary }}
                  aria-hidden
                >
                  {i + 1}
                </span>
                <div className="min-w-0 md:px-2">
                  <span className="flex items-center gap-2 md:justify-center text-slate-800 font-semibold text-sm mb-1">
                    <i className={`${STEP_ICONS[key]} text-lg opacity-80`} style={{ color: theme.primary }} aria-hidden />
                    {t(`compareHub.steps.${key}.title`)}
                  </span>
                  <p className="text-sm text-slate-500 leading-relaxed">{t(`compareHub.steps.${key}.body`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <ComparisonAICommandCentreSection theme={theme} />

      <section className="pb-20 bg-slate-50/50 scroll-mt-24" aria-labelledby="aggregate-heading" id="hub-comparison-table">
        <div className="max-w-5xl mx-auto px-6 pt-4">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div>
              <h2
                id="aggregate-heading"
                className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight mb-3"
              >
                {t('compareHub.tableSection.title')}
              </h2>
              <p className="text-slate-600 max-w-2xl leading-relaxed">{t('compareHub.tableSection.description')}</p>
            </div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-500 shrink-0">
              <i className="ri-shield-check-line" style={{ color: theme.primary }} aria-hidden />
              {t('compareHub.tableSection.badge')}
            </span>
          </div>
          <ComparisonTable
            competitorLabel={competitorLabel}
            rows={displayRows}
            theme={theme}
            id="hub-comparison-table-inner"
          />
        </div>
      </section>

      <FeatureSection
        id="all-vendors"
        title={t('compareHub.vendorsSection.title')}
        variant="white"
        eyebrow={t('compareHub.vendorsSection.eyebrow')}
        theme={theme}
      >
        <p className="text-slate-600 text-[15px] leading-relaxed max-w-3xl mb-8">
          {t('compareHub.vendorsSection.description')}
        </p>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 list-none p-0 m-0 not-prose">
          {COMPETITOR_ALTERNATIVES.map((d) => (
            <li key={d.pathSlug}>
              <Link
                to={`/${d.pathSlug}`}
                className="group flex flex-col h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:border-slate-300/90 hover:shadow-md transition-all"
              >
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                  {t('compareHub.vendorsSection.vsPrefix')} {d.competitorName}
                </span>
                <span className="text-base font-semibold text-slate-900 group-hover:underline decoration-slate-300 underline-offset-2">
                  {d.competitorName} {t('compareHub.vendorsSection.alternativeSuffix')}
                </span>
                <span className="text-sm text-slate-500 mt-2 leading-snug line-clamp-3">{d.heroSubtext}</span>
                <span
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold"
                  style={{ color: theme.primary }}
                >
                  {t('compareHub.vendorsSection.openLink')}
                  <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform" aria-hidden />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </FeatureSection>

      <CTASection
        headline={t('compareHub.cta.headline')}
        subtext={t('compareHub.cta.subtext')}
        pageSlug="compare"
        competitorName="Alternatives"
        theme={theme}
      />

      <FAQSection title={t('compareHub.faqTitle')} items={faqs} theme={theme} />
    </article>
  );
}
